/* eslint-disable no-console */
import {
  CalendarContext,
  CategoryChip,
  isBreakpointMobile,
  ReducerContext,
  Typography,
  useBreakpoint,
} from '@abdoo/common';
import { theme } from '@abdoo/common';
import { Box, Grid } from '@mui/material';
import React, { useCallback, useContext, useState } from 'react';

import { CategoryTotal } from './CalendarTotal/CategoryTotal';

export interface CalendarAnnualSummaryProps {
  isLoading: boolean;
  addAbsence?: boolean;
}

export function CalendarAnnualSummary({
  isLoading,
  addAbsence,
}: CalendarAnnualSummaryProps) {
  const { calendarSummaryValues: calendarSummary } = useContext(CalendarContext);
  const { userSettingsState } = useContext(ReducerContext);

  const calendarYear =
    userSettingsState.start.getMonth() === 0 && userSettingsState.start.getDate() === 1;

  const breakpoint = useBreakpoint();
  const isMobile = isBreakpointMobile(breakpoint);

  const [open, setOpen] = useState(false);
  const closeSummary = useCallback(() => setOpen(false), []);
  const openOrCloseSummary = useCallback(() => setOpen((o) => !o), []);

  const sectionSx = {
    position: 'fixed',
    bottom: 0,
    width: '100%',
    background: 'white',
    textAlign: 'center',
    borderTop: '1px solid #ccc',
    padding: '0.5em 1em',
  };

  const sectionStyles = {
    zIndex: open ? 200 : undefined,
    boxShadow: '0px 0px 30px 5px #888',
  };

  const overlaySx = {
    height: undefined as any as string,
    position: 'fixed',
    textAlign: 'center',
    transition: 'backdrop-filter 0.2s ease-in-out',
    backdropFilter: 'blur(0)',
    width: '100%',
    top: 0,
  };
  const overlayStyles = {
    zIndex: '100',
  };

  if (isMobile && open && !addAbsence) {
    overlaySx.height = '100%';
    overlaySx.backdropFilter = 'blur(4px)';
  }

  return (
    <>
      <Box
        component="div"
        style={overlayStyles}
        sx={overlaySx}
        onClick={closeSummary}
      ></Box>
      <Box
        component="section"
        style={sectionStyles}
        sx={sectionSx}
        onClick={openOrCloseSummary}
      >
        <Grid container justifyContent="center">
          <Grid item xs={12} md={4}>
            <Typography
              variant="body1"
              color={theme.palette.primary.main}
              isLoading={isLoading}
              skeletonSx={{ width: '7rem', margin: 'auto' }}
            >
              {calendarYear
                ? `${userSettingsState.start.getFullYear()}`
                : `${userSettingsState.start.getFullYear()}/${
                    userSettingsState.start.getFullYear() + 1
                  }`}
            </Typography>
            {calendarSummary ? (
              addAbsence ? (
                <>
                  <Typography component="div" isLoading={false}>
                    <small>
                      You have{' '}
                      <CategoryChip
                        categoryName="remaining"
                        value={calendarSummary.remaining}
                        isLoading={false}
                      />{' '}
                      days remaining.
                    </small>
                  </Typography>
                  <Typography isLoading={false}>
                    <small>Please click the calendar to book an absence.</small>
                  </Typography>
                </>
              ) : (
                <>
                  {(!isMobile || open) && (
                    <Typography
                      variant="body2"
                      color={theme.palette.primary.main}
                      isLoading={isLoading}
                      skeletonSx={{ width: '9rem', margin: 'auto' }}
                    >
                      Entitlement: {calendarSummary.entitlement} days
                    </Typography>
                  )}
                  <CategoryTotal
                    categoryName="remaining"
                    value={calendarSummary.remaining}
                    open={!isMobile || open}
                    isLoading={isLoading}
                  />
                  <CategoryTotal
                    categoryName="taken"
                    value={calendarSummary.approvedTaken}
                    open={!isMobile || open}
                    isLoading={isLoading}
                  />
                  <CategoryTotal
                    categoryName="approved"
                    value={calendarSummary.approvedFuture}
                    open={!isMobile || open}
                    isLoading={isLoading}
                  />
                  <CategoryTotal
                    categoryName="pending"
                    value={calendarSummary.pending}
                    open={!isMobile || open}
                    isLoading={isLoading}
                  />
                </>
              )
            ) : (
              <>Loading Summary</>
            )}
          </Grid>
        </Grid>
      </Box>
    </>
  );
}
